import * as React from "react";
import { css } from "@emotion/react";
import { Link } from "react-router-dom";
import tw, { styled } from "twin.macro";

import SubHeader from "app/styles/SubHeader";
import Text from "app/styles/Text";
import { isString } from "app/utils/helpers";

export interface CardAction {
  label?: string;
  url?: string;
  onAction?: () => void;
}
export interface CardProps
  extends Omit<React.BaseHTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  primaryAction?: CardAction;
  secondaryAction?: React.ReactNode;
}

interface CardElProps {
  blackOnDark?: boolean;
  error?: boolean;
  focused?: boolean;
}

function Card(props: React.PropsWithChildren<CardProps & CardElProps>) {
  const { title, subTitle, primaryAction, secondaryAction, ...rest } = props;

  const showHeader = Boolean(title || subTitle);

  return (
    <CardContainer {...rest}>
      {showHeader ? (
        <div className="header">
          <div>
            {!!title ? (
              isString(title) ? (
                <SubHeader big>{title}</SubHeader>
              ) : (
                title
              )
            ) : null}

            {!!subTitle ? (
              isString(subTitle) ? (
                <Text>{subTitle}</Text>
              ) : (
                subTitle
              )
            ) : null}
          </div>

          <div>
            {primaryAction ? (
              primaryAction.url ? (
                <ActionLink to={primaryAction.url}>
                  {primaryAction.label}
                </ActionLink>
              ) : (
                <ActionButton onClick={primaryAction.onAction}>
                  {primaryAction.label}
                </ActionButton>
              )
            ) : null}
            {secondaryAction && secondaryAction}
          </div>
        </div>
      ) : null}

      {props.children}
    </CardContainer>
  );
}

const CardContainer = styled.div<CardElProps>`
  background-color: #fff;
  ${tw`dark:bg-[#0D0F12]`};
  border: 1px solid rgba(91, 97, 110, 0.2);
  border-radius: 8px;

  ${({ focused }) =>
    focused &&
    css`
      ${tw`border-2 border-[#1A1A1A] dark:border-[#E5E5E5]`}
    `}

  ${(props) =>
    props.error &&
    css`
      border-color: #ff0000;
    `};

  ${(props) =>
    props.blackOnDark &&
    css`
      ${tw`dark:bg-black`};
    `};

  > .header {
    display: flex;
    padding: 16px 20px;
    justify-content: space-between;
    border-bottom: 1px solid rgba(91, 97, 110, 0.2);

    > div:last-of-type {
      margin-top: 5px;
    }

    p {
      ${tw`text-black60 dark:text-white60`}
    }
  }
`;

const actionStyles = css`
  color: #12a633;
  font-size: 1.4rem;
  font-weight: 600;
`;

const ActionLink = styled(Link)`
  ${actionStyles};
`;

const ActionButton = styled.button`
  ${actionStyles};
`;

export default Card;
